import { useCallback, useEffect, useState } from "react";
import { Button, Modal, TextInput } from "@monolith-forensics/monolith-ui";
import { useSnackbar } from "notistack";
import ButtonMenu from "@/components/Forms/components/ButtonMenu";
import type { File } from "@/Features/Files/files.types";
import { useRenameEvidencePhoto } from "@/Features/EvidencePhotos";
import { isEvidencePhoto } from "./evidence-photos.utils";
import { EvidencePhotoModalAlert } from "./EvidencePhotoModalAlert";

// Name only, like the legacy tab had no description either.
export const RenameEvidencePhotoModal: React.FC<{
  show: boolean;
  evidenceId: number;
  file: File;
  onClose?: () => void;
  onSubmit?: () => void;
}> = ({ show, evidenceId, file, onClose, onSubmit }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { mutate: renameEvidencePhoto } = useRenameEvidencePhoto();
  const [name, setName] = useState(file.name);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError("File name is required");
      return;
    }
    // The tab lists photos by extension, so a name without one would drop the
    // photo out of the only place that shows it while the flag is off.
    if (!isEvidencePhoto({ name: trimmed })) {
      setError("Keep a photo extension such as .jpg");
      return;
    }
    if (trimmed === file.name) {
      onClose?.();
      return;
    }
    setError(null);
    setSubmitting(true);
    renameEvidencePhoto(
      { evidence_id: evidenceId, photo_uuid: file.uuid, name: trimmed },
      {
        onSuccess: () => {
          setSubmitting(false);
          enqueueSnackbar("Photo renamed.", { variant: "success" });
          onSubmit?.();
        },
        // The axios interceptor already toasted the HTTP error.
        onError: (err) => {
          setSubmitting(false);
          console.error(err);
        },
      }
    );
  };

  const handleCancel = useCallback(() => {
    if (submitting) return;
    onClose?.();
  }, [submitting, onClose]);

  useEffect(() => {
    if (!show) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleCancel();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [show, handleCancel]);

  return (
    <Modal open={show} onClose={handleCancel} showCloseButton={true}>
      <Modal.Title>Rename Photo</Modal.Title>
      <EvidencePhotoModalAlert kind="rename" />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
        style={{ margin: "20px 0px", width: "350px" }}
      >
        <TextInput
          value={name}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setName(e.target.value)
          }
          label="File Name"
          required={true}
          error={error || undefined}
          autoFocus
        />
      </form>
      <ButtonMenu>
        <Button
          size="xs"
          variant="subtle"
          onClick={handleCancel}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button
          size="xs"
          color="primary"
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          loading={submitting}
        >
          Rename
        </Button>
      </ButtonMenu>
    </Modal>
  );
};
